import { useCallback, useEffect, useState } from 'react';
import { extractActivitiesFromResponse, fetchActivities } from '../api/activities';
import {
  createContent,
  deleteContent,
  extractContentFromResponse,
  extractContentsFromResponse,
  fetchContents,
  getContentActivityLabel,
  getContentImageUrl,
} from '../api/contents';
import { getApiErrorMessage } from '../api/errors';
import AdminIconButton from './AdminIconButton';
import AdminModal from './AdminModal';
import AdminPagination from './AdminPagination';
import AdminTableLoader from './AdminTableLoader';
import { confirmDelete, showError, showSuccess } from './adminAlerts';
import { usePagination } from './usePagination';
import './adminShared.css';

function AdminContent() {
  const [activityId, setActivityId] = useState('');
  const [activities, setActivities] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [contents, setContents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);
  const [isLoadingContents, setIsLoadingContents] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const loadActivities = useCallback(async () => {
    setIsLoadingActivities(true);

    try {
      const response = await fetchActivities();
      const items = extractActivitiesFromResponse(response);
      setActivities(items);
      setActivityId((current) => current || (items[0] ? String(items[0].id) : ''));
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not load activities.'));
    } finally {
      setIsLoadingActivities(false);
    }
  }, []);

  const loadContents = useCallback(async () => {
    setIsLoadingContents(true);

    try {
      const response = await fetchContents();
      setContents(extractContentsFromResponse(response));
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not load content.'));
    } finally {
      setIsLoadingContents(false);
    }
  }, []);

  useEffect(() => {
    loadActivities();
    loadContents();
  }, [loadActivities, loadContents]);

  const closeModal = () => {
    setShowAddModal(false);
    setImageFile(null);
  };

  const openAddModal = () => {
    if (activities.length === 0) {
      showError('Create an activity first.');
      return;
    }
    setShowAddModal(true);
    setImageFile(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!activityId) {
      showError('Choose an activity.');
      return;
    }
    if (!imageFile) {
      showError('Choose an image to upload.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createContent({ activityId, imageFile });
      const content = extractContentFromResponse(response);
      setContents((prev) => [content, ...prev]);
      closeModal();
      showSuccess('Content uploaded.');
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not upload content.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete({
      title: 'Delete content?',
      text: 'This photo will be removed permanently.',
    });
    if (!confirmed) return;

    setBusyId(id);

    try {
      await deleteContent(id);
      setContents((prev) => prev.filter((item) => item.id !== id));
      showSuccess('Content deleted.');
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not delete content.'));
    } finally {
      setBusyId(null);
    }
  };

  const {
    paginatedItems,
    page,
    setPage,
    totalPages,
    totalItems,
    rangeStart,
    rangeEnd,
  } = usePagination(contents);

  return (
    <div className="admin-page">
      <div className="admin-page__top">
        <div>
          <h1 className="admin-page__title">Content</h1>
          <p className="admin-page__subtitle">Upload photos for an activity.</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openAddModal}>
          + Add
        </button>
      </div>

      {isLoadingContents ? (
        <AdminTableLoader
          headers={['ID', 'Image', 'Activity', 'Actions']}
          columns={['id', 'thumb', 'title', 'actions']}
        />
      ) : contents.length === 0 ? (
        <p className="admin-panel__note">No content yet.</p>
      ) : (
        <section className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Image</th>
                <th>Activity</th>
                <th className="admin-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((content, index) => {
                const imageUrl = getContentImageUrl(content);

                return (
                  <tr key={content.id ?? `content-${index}`}>
                    <td className="admin-table__id">{content.id}</td>
                    <td>
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className="admin-table__thumb" />
                      ) : (
                        <span className="admin-table__thumb--empty" aria-hidden="true" />
                      )}
                    </td>
                    <td>
                      <span className="admin-table__title">
                        {getContentActivityLabel(content)}
                      </span>
                    </td>
                    <td className="admin-table__actions">
                      <AdminIconButton
                        label="Delete content"
                        variant="danger"
                        onClick={() => handleDelete(content.id)}
                        disabled={busyId === content.id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <AdminPagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onPageChange={setPage}
          />
        </section>
      )}

      {showAddModal && (
        <AdminModal title="Add content" onClose={closeModal}>
          <form className="admin-form-grid" onSubmit={handleSubmit}>
            <label className="admin-field">
              <span className="admin-field__label">Activity</span>
              <select
                className="admin-field__input"
                value={activityId}
                onChange={(e) => setActivityId(e.target.value)}
                required
                disabled={isSubmitting || isLoadingActivities}
              >
                {activities.map((activity) => (
                  <option key={activity.id} value={activity.id}>
                    {activity.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Image</span>
              <input
                type="file"
                className="admin-field__input admin-field__file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                required
                disabled={isSubmitting}
              />
              <span className="admin-field__hint">
                Recommended size: 1080 × 1080 px (square).
              </span>
            </label>
            {previewUrl && (
              <img
                src={previewUrl}
                alt=""
                className="admin-preview-img--content"
              />
            )}
            <div className="admin-actions">
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Uploading…' : 'Upload'}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
}

export default AdminContent;
